import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../utils/seo';
import Icon from '../../components/base/Icon';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d6pfj1ua739gopmlgqhg';

const SERVICE_OPTIONS = [
  { value: '', label: 'Select a service...' },
  { value: 'Fence Installation & Repair', label: 'Fence Installation & Repair' },
  { value: 'Handyman Services', label: 'Handyman Services' },
  { value: 'Bathroom Remodel', label: 'Bathroom Remodel' },
  { value: 'Small Remodel Project', label: 'Small Remodel Project' },
  { value: 'Fix & Flip Renovation', label: 'Fix & Flip Renovation' },
  { value: 'Other', label: 'Other / Not Sure Yet' },
];

const SERVICE_AREAS = [
  'Boise', 'Meridian', 'Nampa', 'Caldwell', 'Eagle',
  'Star', 'Kuna', 'Middleton', 'Garden City', 'Beacon Light',
];

interface ContactFormState {
  full_name: string;
  phone: string;
  email: string;
  city: string;
  service_needed: string;
  project_details: string;
}

const emptyForm = (): ContactFormState => ({
  full_name: '',
  phone: '',
  email: '',
  city: '',
  service_needed: '',
  project_details: '',
});

function ContactForm() {
  const [values, setValues] = useState<ContactFormState>(emptyForm());
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const validate = (): string => {
    if (!values.full_name.trim()) return 'Full name is required.';
    if (!values.phone.trim()) return 'Phone number is required.';
    if (!values.email.trim()) return 'Email address is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) return 'Please enter a valid email address.';
    if (!values.city.trim()) return 'City is required.';
    if (!values.service_needed) return 'Please select a service.';
    if (!values.project_details.trim()) return 'Please describe your project.';
    if (values.project_details.length > 500) return 'Project details must be 500 characters or fewer.';
    return '';
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setErrorMsg(validationError);
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const params = new URLSearchParams();
      (Object.keys(values) as (keyof ContactFormState)[]).forEach((key) => {
        params.append(key, values[key]);
      });
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (!res.ok) throw new Error('Submission failed');
      setStatus('success');
      setValues(emptyForm());
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again or call us directly at (208) 204-6331.');
    }
  };

  const inputClass =
    'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#B45309] focus:border-transparent text-sm bg-white';

  if (status === 'success') {
    return (
      <div className="text-center py-12 px-6">
        <span className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
          <Icon name="check" className="w-8 h-8 text-green-600" />
        </span>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Request Received!</h3>
        <p className="text-gray-600 text-sm mb-6">
          Thanks — we&apos;ll review your project details and get back to you within <strong>24 hours</strong>. If you need a faster response, call us directly.
        </p>
        <a
          href="tel:+12082046331"
          className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm"
        >
          <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
          Call (208) 204-6331
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} data-readdy-form id="contact-page-quote-form" noValidate>
      {status === 'error' && errorMsg && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2" role="alert">
          <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon name="circle-exclamation" className="w-4 h-4" />
          </span>
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="contact-full_name" className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="contact-full_name"
            name="full_name"
            required
            value={values.full_name}
            onChange={handleChange}
            className={inputClass}
            placeholder="John Smith"
          />
        </div>
        <div>
          <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-1">
            Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="contact-phone"
            name="phone"
            required
            value={values.phone}
            onChange={handleChange}
            className={inputClass}
            placeholder="(208) 555-0123"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="contact-email"
            name="email"
            required
            value={values.email}
            onChange={handleChange}
            className={inputClass}
            placeholder="john@example.com"
          />
        </div>
        <div>
          <label htmlFor="contact-city" className="block text-sm font-medium text-gray-700 mb-1">
            Your City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="contact-city"
            name="city"
            required
            value={values.city}
            onChange={handleChange}
            className={inputClass}
            placeholder="e.g. Meridian, Nampa, Eagle..."
          />
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="contact-service_needed" className="block text-sm font-medium text-gray-700 mb-1">
          Service Needed <span className="text-red-500">*</span>
        </label>
        <select
          id="contact-service_needed"
          name="service_needed"
          required
          value={values.service_needed}
          onChange={handleChange}
          className={`${inputClass} cursor-pointer`}
        >
          {SERVICE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      <div className="mb-6">
        <label htmlFor="contact-project_details" className="block text-sm font-medium text-gray-700 mb-1">
          Project Details <span className="text-red-500">*</span>
        </label>
        <textarea
          id="contact-project_details"
          name="project_details"
          required
          rows={4}
          maxLength={500}
          value={values.project_details}
          onChange={handleChange}
          className={`${inputClass} resize-none`}
          placeholder="Describe your project — what needs to be done, approximate size, any timeline requirements, and anything else that would help us give you an accurate estimate..."
        />
        <p className="text-xs text-gray-500 mt-1 text-right">{values.project_details.length}/500 characters</p>
      </div>

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full bg-[#B45309] hover:bg-[#92400E] disabled:opacity-60 text-white px-6 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2 text-base"
      >
        {status === 'submitting' ? (
          <>
            <span className="w-5 h-5 flex items-center justify-center animate-spin">
              <Icon name="circle-notch" className="w-4 h-4" />
            </span>
            Sending...
          </>
        ) : (
          <>
            Request Free Estimate
            <span className="w-5 h-5 flex items-center justify-center">
              <Icon name="arrow-right" className="w-4 h-4" />
            </span>
          </>
        )}
      </button>

      <p className="text-xs text-gray-500 text-center mt-3">
        No spam. No obligation. We respond within 24 hours.
      </p>
    </form>
  );
}

const contactFaqs = [
  {
    q: 'How do I request a free estimate from Fix Right Services?',
    a: 'The fastest way is to call or text us at (208) 204-6331 — we respond the same day. You can also fill out the quote request form on this page and we\'ll follow up within 24 hours. Either way, the next step is a brief conversation about your project and a free on-site visit before we deliver a written estimate.',
  },
  {
    q: 'How quickly will you respond after I submit the form?',
    a: 'We respond to every form submission within 24 hours — usually the same business day. If you need a faster response, calling or texting (208) 204-6331 is the quickest route. We\'re available Monday–Friday 8am–6pm and Saturday 9am–3pm.',
  },
  {
    q: 'Is the estimate really free and no-obligation?',
    a: 'Yes — completely free, completely no-obligation. We come out, assess the project, and deliver a written, itemized estimate within 24–48 hours of the site visit. There\'s no pressure to move forward, and we won\'t follow up repeatedly if you decide it\'s not the right time.',
  },
  {
    q: 'What information should I have ready when I contact you?',
    a: 'The more detail you can share, the better — but don\'t worry if you\'re not sure yet. Helpful things to have: the type of project (fence, handyman, remodel), approximate size or scope, your city or neighborhood, and any timeline requirements. If you have photos, those are always useful. We\'ll ask the right questions during our follow-up call.',
  },
  {
    q: 'Do you serve my city in the Treasure Valley?',
    a: 'Our primary service areas are Boise, Meridian, Nampa, Caldwell, Eagle, Star, Kuna, Middleton, Garden City, and Beacon Light. We also take select projects in Emmett and Mountain Home. If you\'re outside these areas, call us at (208) 204-6331 and we\'ll let you know whether we can help.',
  },
  {
    q: 'Can I contact you on weekends?',
    a: 'Yes — we\'re available Saturday 9am–3pm. You can call, text, or submit the form any time, and we\'ll respond during business hours. For urgent situations, calling or texting directly is the best option.',
  },
  {
    q: 'What services can I request a quote for?',
    a: 'We provide free estimates for fence installation and repair (wood, vinyl, chain link), handyman services (drywall, painting, fixtures, maintenance), bathroom remodels (full gut-and-rebuild or targeted upgrades), and small remodel projects (kitchen updates, basement finishing, flooring, fix-and-flip work). We do not do roofing — if that\'s what you need, we\'ll refer you to someone we trust.',
  },
  {
    q: 'Will I be dealing with the owner directly, or a salesperson?',
    a: 'You\'ll deal directly with Kole Southern, the owner of Fix Right Services. There\'s no sales team, no middleman, and no handoff to a crew you\'ve never met. Kole handles the estimate, is on-site during the work, and is reachable throughout the project. That\'s how we keep quality consistent.',
  },
  {
    q: 'What happens after I submit the contact form?',
    a: 'Here\'s the exact process: (1) You submit the form. (2) We review your project details and reach out within 24 hours to confirm and ask any follow-up questions. (3) We schedule a free on-site visit at a time that works for you. (4) Within 24–48 hours of that visit, you receive a written, itemized estimate. No verbal guesses, no pressure.',
  },
  {
    q: 'Can I get a rough price estimate over the phone before the site visit?',
    a: 'We can give you a general ballpark range over the phone for common projects — for example, a typical wood privacy fence or a standard drywall repair. However, we don\'t provide firm quotes without seeing the project in person. Too many variables affect the final number, and we\'d rather give you an accurate written estimate than a guess that turns out to be wrong.',
  },
];

export default function ContactPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useSEO({
    title: 'Contact Fix Right Services | Free Estimate — Boise, ID (208) 204-6331',
    description: 'Contact Fix Right Services for a free estimate on fence installation, handyman repairs, or bathroom remodels in Boise & Treasure Valley. Call (208) 204-6331 or submit a quote request.',
    keywords: 'contact Fix Right Services Boise, free estimate fence Boise Idaho, handyman quote Treasure Valley, remodel contractor contact Boise, fence installation estimate Meridian Idaho',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/contact`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      'name': 'Contact Fix Right Services',
      'url': `${import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com'}/contact`,
      'description': 'Request a free estimate for fence installation, handyman services, or remodeling in Boise and the Treasure Valley.',
      'mainEntity': {
        '@type': 'LocalBusiness',
        'name': 'Fix Right Services',
        'telephone': '+12082046331',
        'email': 'Southernkole@gmail.com',
        'url': import.meta.env.VITE_SITE_URL || 'https://fixrightservices.com',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Boise',
          'addressRegion': 'ID',
          'addressCountry': 'US',
        },
        'openingHoursSpecification': [
          {
            '@type': 'OpeningHoursSpecification',
            'dayOfWeek': ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            'opens': '08:00',
            'closes': '18:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            'dayOfWeek': 'Saturday',
            'opens': '09:00',
            'closes': '15:00',
          },
        ],
      },
    },
  });

  return (
    <div className="min-h-screen bg-white">

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-8">
              <Link to="/">
                <img
                  src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                  alt="Fix Right Services Logo"
                  width="180"
                  height="56"
                  className="h-14 w-auto"
                  loading="eager"
                />
              </Link>
              <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-700">
                <Link to="/" className="hover:text-[#B45309] transition-colors cursor-pointer">Home</Link>
                <Link to="/services" className="hover:text-[#B45309] transition-colors cursor-pointer">Services</Link>
                <Link to="/about" className="hover:text-[#B45309] transition-colors cursor-pointer">About</Link>
                <Link to="/blog" className="hover:text-[#B45309] transition-colors cursor-pointer">Blog</Link>
                <Link to="/contact" className="text-[#B45309] font-semibold cursor-pointer">Contact</Link>
              </div>
            </div>
            <a
              href="tel:+12082046331"
              className="flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
              aria-label="Call Fix Right Services"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              <span className="hidden sm:inline">(208) 204-6331</span>
              <span className="sm:hidden">Call Now</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="Contact hero">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edbedc3058bbac7f2291605f6582853d.jpg"
            alt="Fix Right Services — Contact Us"
            width="1920"
            height="650"
            className="w-full h-full object-cover object-top"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/65"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto w-full">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-white/70">
              <li><Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link></li>
              <li><span className="w-4 h-4 inline-flex items-center justify-center"><Icon name="chevron-right" className="w-3 h-3" /></span></li>
              <li className="text-white font-medium">Contact</li>
            </ol>
          </nav>
          <div className="inline-flex items-center gap-2 bg-[#B45309]/90 text-white text-sm font-medium px-4 py-2 rounded-full mb-6">
            <span className="w-4 h-4 flex items-center justify-center"><Icon name="location-dot" className="w-3.5 h-3.5" /></span>
            Boise &amp; Treasure Valley, Idaho
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
            Get Your Free Estimate
          </h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl">
            Call us directly or fill out the form below. We respond to every inquiry within <strong className="text-white">24 hours</strong> with a free, written, no-obligation estimate.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="tel:+12082046331"
              className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
              Call (208) 204-6331
            </a>
            <a
              href="#contact-form"
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-900 px-8 py-3.5 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center"><Icon name="file-lines" className="w-4 h-4" /></span>
              Submit a Request
            </a>
          </div>
        </div>
      </section>

      {/* Main Content: Form + Sidebar */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" id="contact-form" aria-label="Contact form and info">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 items-start">

            {/* Form — wider column */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
                <div className="mb-7">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Request a Free Quote</h2>
                  <p className="text-gray-600 text-sm">
                    Fill out the form and we&apos;ll get back to you within <strong>24 hours</strong> — usually sooner.
                  </p>
                </div>
                <ContactForm />
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">

              {/* Primary CTA — Call */}
              <div className="bg-[#B45309] rounded-2xl p-7 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
                    <Icon name="phone" className="w-6 h-6 text-white" />
                  </span>
                  <div>
                    <p className="text-white/80 text-xs font-medium uppercase tracking-wide">Fastest Response</p>
                    <p className="font-bold text-lg">Call or Text Us</p>
                  </div>
                </div>
                <a
                  href="tel:+12082046331"
                  className="block text-3xl font-bold text-white hover:text-white/90 transition-colors cursor-pointer mb-1"
                >
                  (208) 204-6331
                </a>
                <p className="text-white/80 text-sm mb-5">Mon–Fri 8am–6pm &nbsp;|&nbsp; Sat 9am–3pm</p>
                <a
                  href="tel:+12082046331"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#B45309] hover:bg-gray-50 px-5 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><Icon name="phone" className="w-3.5 h-3.5" /></span>
                  Tap to Call Now
                </a>
              </div>

              {/* Email */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg">
                    <Icon name="envelope" className="w-5 h-5 text-[#B45309]" />
                  </span>
                  <h3 className="font-bold text-gray-900">Email Us</h3>
                </div>
                <a
                  href="mailto:Southernkole@gmail.com"
                  className="text-[#B45309] font-semibold text-sm hover:underline cursor-pointer break-all"
                >
                  Southernkole@gmail.com
                </a>
                <p className="text-gray-500 text-xs mt-1">We reply within 24 hours</p>
              </div>

              {/* Response Time */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg">
                    <Icon name="clock" className="w-5 h-5 text-[#B45309]" />
                  </span>
                  <h3 className="font-bold text-gray-900">Response Time</h3>
                </div>
                <ul className="space-y-3 text-sm">
                  {[
                    { label: 'Phone / Text', value: 'Same day', icon: 'phone' },
                    { label: 'Form submission', value: 'Within 24 hours', icon: 'file-lines' },
                    { label: 'Written estimate', value: '24–48 hrs after site visit', icon: 'file-contract' },
                  ].map((item) => (
                    <li key={item.label} className="flex items-center justify-between gap-3">
                      <span className="flex items-center gap-2 text-gray-600">
                        <span className="w-4 h-4 flex items-center justify-center">
                          <Icon name={item.icon} className="w-3.5 h-3.5 text-gray-400" />
                        </span>
                        {item.label}
                      </span>
                      <span className="font-semibold text-gray-900 text-xs whitespace-nowrap">{item.value}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Credentials */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg">
                    <Icon name="shield-halved" className="w-5 h-5 text-[#B45309]" />
                  </span>
                  <h3 className="font-bold text-gray-900">Credentials</h3>
                </div>
                <ul className="space-y-3 text-sm">
                  {[
                    { label: 'Licensed in Idaho', value: 'Yes', icon: 'check' },
                    { label: 'General Liability Insurance', value: 'Yes', icon: 'check' },
                    { label: 'Contractor Registration', value: 'Provided upon request', icon: 'file-contract' },
                    { label: 'Owner on every job', value: 'Kole Southern', icon: 'user-check' },
                  ].map((item) => (
                    <li key={item.label} className="flex items-start justify-between gap-3">
                      <span className="flex items-center gap-2 text-gray-600">
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <Icon name={item.icon} className="w-3.5 h-3.5 text-[#B45309]" />
                        </span>
                        {item.label}
                      </span>
                      <span className="font-semibold text-gray-900 text-xs text-right">{item.value}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-gray-500 mt-4 border-t border-gray-100 pt-3">
                  Proof of insurance and registration available before any work begins — just ask.
                </p>
              </div>

              {/* Social */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-4 text-sm">Follow Us</h3>
                <div className="flex gap-3">
                  <a
                    href="https://www.facebook.com/profile.php?id=61582658047880"
                    aria-label="Fix Right Services on Facebook"
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    className="flex items-center gap-2 flex-1 justify-center bg-gray-50 hover:bg-[#B45309]/10 border border-gray-200 hover:border-[#B45309]/30 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:text-[#B45309] transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center"><Icon name="facebook" className="w-4 h-4" /></span>
                    Facebook
                  </a>
                  <a
                    href="https://www.instagram.com/fixright_services/"
                    aria-label="Fix Right Services on Instagram"
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    className="flex items-center gap-2 flex-1 justify-center bg-gray-50 hover:bg-[#B45309]/10 border border-gray-200 hover:border-[#B45309]/30 rounded-lg py-2.5 text-sm font-medium text-gray-700 hover:text-[#B45309] transition-colors cursor-pointer"
                  >
                    <span className="w-5 h-5 flex items-center justify-center"><Icon name="instagram" className="w-4 h-4" /></span>
                    Instagram
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Service areas">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Where We Work</h2>
            <p className="text-gray-600 text-sm">
              We serve homeowners and investors across the Treasure Valley. Not sure if we cover your area? Just call.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {SERVICE_AREAS.map((area) => (
              <div
                key={area}
                className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-5 py-2.5 text-sm font-medium text-gray-700 hover:border-[#B45309] hover:text-[#B45309] transition-colors"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <Icon name="location-dot" className="w-3.5 h-3.5 text-[#B45309]" />
                </span>
                {area}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500">
            Also serving Emmett, Mountain Home, and surrounding areas — call{' '}
            <a href="tel:+12082046331" className="text-[#B45309] font-semibold hover:underline cursor-pointer">(208) 204-6331</a>{' '}
            to confirm availability.
          </p>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" aria-label="What to expect">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">What Happens After You Reach Out</h2>
            <p className="text-gray-600 text-sm">No runaround. Here&apos;s exactly what to expect.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                icon: 'paper-plane',
                title: 'You Contact Us',
                desc: 'Call, text, or submit the form above. Takes less than 2 minutes.',
              },
              {
                step: '02',
                icon: 'clock',
                title: 'We Respond Fast',
                desc: 'You\'ll hear from us within 24 hours — usually the same day.',
              },
              {
                step: '03',
                icon: 'magnifying-glass',
                title: 'Free Site Visit',
                desc: 'We come out, assess the project, and ask the right questions.',
              },
              {
                step: '04',
                icon: 'file-contract',
                title: 'Written Estimate',
                desc: 'You get a clear, itemized written estimate within 24–48 hours. No verbal guesses.',
              },
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-md transition-shadow text-center">
                <div className="relative inline-flex mb-5">
                  <span className="w-14 h-14 flex items-center justify-center bg-[#B45309]/10 rounded-xl">
                    <Icon name={item.icon} className="w-7 h-7 text-[#B45309]" />
                  </span>
                  <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-[#B45309] text-white text-xs font-bold rounded-full">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{item.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" aria-label="Frequently asked questions" id="contact-faq">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-600">Common questions about reaching out, getting an estimate, and what to expect.</p>
          </div>
          <div className="space-y-3">
            {contactFaqs.map((faq, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-100 transition-colors cursor-pointer"
                  aria-expanded={openFaq === idx}
                >
                  <h4 className="font-semibold text-gray-900 text-sm pr-4">
                    <a id={`faq-contact-${idx}`}>{faq.q}</a>
                  </h4>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-[#B45309]">
                    <Icon name={openFaq === idx ? 'chevron-up' : 'chevron-down'} className="w-4 h-4" />
                  </span>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                'url': 'https://fixrightservices.com/contact',
                'mainEntity': contactFaqs.map((faq) => ({
                  '@type': 'Question',
                  'name': faq.q,
                  'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': faq.a,
                  },
                })),
              }),
            }}
          />

          <div className="mt-10 text-center">
            <p className="text-gray-600 text-sm mb-5">Still have a question? We&apos;re happy to help.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:+12082046331"
                className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                <span className="w-5 h-5 flex items-center justify-center"><Icon name="phone" className="w-4 h-4" /></span>
                Call (208) 204-6331
              </a>
              <a
                href="#contact-form"
                className="inline-flex items-center gap-2 border border-gray-300 hover:border-[#B45309] text-gray-700 hover:text-[#B45309] px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                <span className="w-5 h-5 flex items-center justify-center"><Icon name="file-lines" className="w-4 h-4" /></span>
                Submit a Request
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src="https://static.readdy.ai/image/c174e3e7fa028bcf803982b5ec8aa9e6/2ea97a783d40b0afc97fadf5f7236f78.webp"
                alt="Fix Right Services"
                width="180"
                height="48"
                className="h-12 w-auto mb-4"
                loading="lazy"
              />
              <p className="text-gray-400 text-sm">
                Boise&apos;s trusted experts for fence installation, handyman services, and home remodels. Licensed &amp; insured in Idaho.
              </p>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Services</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/services/fence" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Fence Installation &amp; Repair</Link></li>
                <li><Link to="/services/handyman-faq" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Handyman Services</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Bathroom Remodels</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Small Remodel Projects</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About Us</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Blog &amp; Tips</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <Icon name="phone" className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </span>
                  <a href="tel:+12082046331" className="hover:text-white transition-colors cursor-pointer">(208) 204-6331</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <Icon name="envelope" className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </span>
                  <a href="mailto:Southernkole@gmail.com" className="hover:text-white transition-colors cursor-pointer">Southernkole@gmail.com</a>
                </li>
                <li className="flex items-start gap-2 text-gray-400">
                  <span className="w-4 h-4 flex items-center justify-center mt-0.5">
                    <Icon name="location-dot" className="w-3.5 h-3.5 text-[#F59E0B]" />
                  </span>
                  <span>Boise, Meridian, Nampa, Caldwell &amp; Treasure Valley</span>
                </li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a
                  href="https://www.facebook.com/profile.php?id=61582658047880"
                  aria-label="Facebook"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                >
                  <Icon name="facebook" className="w-5 h-5" />
                </a>
                <a
                  href="https://www.instagram.com/fixright_services/"
                  aria-label="Instagram"
                  rel="nofollow noopener noreferrer"
                  target="_blank"
                  className="w-10 h-10 bg-gray-800 hover:bg-[#B45309] rounded-lg flex items-center justify-center transition-colors cursor-pointer"
                >
                  <Icon name="instagram" className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-400">
            <p>&copy; 2025 Fix Right Services. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
